import { parseBraceOrCsv, compressGuidToBase64Url, encodeArrayField, decompressGuidFromBase64Url } from '../core/EncodeValues.js';
import { FilterFieldTypes } from './FilterFieldMap.js';
import { PresetRegistry } from './PresetRegistry.js';

/**
 * Кодек синхронизации состояния фильтра с query string.
 * Sync: entries → URL.
 * Decode/prime: URL → состояние/примирование для init/popstate.
 */
class SearchUrlCodec {
    constructor() {
        /** Последнее состояние параметров, записанных кодеком. */
        this._lastParams = {};
    }
    /**
     * Синхронизировать entries с URL.
     * Делает всё: encode → удалить отключившиеся → выбрать push/replace → commit.
     * @param entries Полный список полей (схемы всегда, value опционально)
     */
    sync(entries) {
        const { params, removedKeys, changedKeys, schemaByUrlKey } = this._encodeAndDiff(entries);
        const mode = this._resolveCommitMode(entries, changedKeys, removedKeys);
        const sp = this._cloneSearch(location.search);
        let pathname = location.pathname;
        const { spNext, pathnameNext, nextMode } = this._apply(sp, pathname, params, removedKeys, schemaByUrlKey, mode);
        const nextUrl = this._buildUrl(pathnameNext, spNext);
        if (nextUrl !== this._currentUrl())
            this._commit(nextUrl, nextMode);
        this._lastParams = params;
        return nextMode;
    }
    /**
     * Закодировать entries и вычислить diff относительно предыдущего состояния.
     * @param entries Полный список полей
     */
    _encodeAndDiff(entries) {
        const params = {};
        const scopeKeys = [];
        const schemaByUrlKey = new Map();
        for (const e of entries) {
            const schema = e.schema;
            const urlKey = schema.urlKey ?? schema.key;
            schemaByUrlKey.set(urlKey, schema);
            scopeKeys.push(urlKey);
            if (!e.value)
                continue;
            const encoded = this._encodeValue(e.value.value, schema);
            if (encoded == null)
                continue;
            params[urlKey] = encoded;
        }
        const removedKeys = [];
        const changedKeys = [];
        const scope = new Set(scopeKeys);
        // removed: всё из scope, чего нет в новых params
        if (this._lastParams)
            for (const k of Object.keys(this._lastParams)) {
                // Если удаляемых ключей небыло, то это не считается изменением
                if (!scope.has(k))
                    continue;
                if (!(k in params)) {
                    removedKeys.push(k);
                }
            }
        // changed: сравнение новых params с lastParams
        const all = new Set([...Object.keys(this._lastParams), ...Object.keys(params)]);
        for (const k of all) {
            const a = this._lastParams[k];
            const b = params[k];
            if (a !== b) {
                changedKeys.push(k);
            }
        }
        return { params, removedKeys, changedKeys, schemaByUrlKey };
    }
    /**
     * Выбрать режим commit на основании схем изменившихся/удалённых ключей.
     * Приоритет: если среди затронутых полей есть push → push, иначе replace.
     * @param entries Полный список полей
     * @param changedKeys Ключи, значения которых изменились
     * @param removedKeys Ключи, которые удаляются из URL
     */
    _resolveCommitMode(entries, changedKeys, removedKeys) {
        const touched = new Set([...changedKeys, ...removedKeys]);
        // urlKey->schema
        const schemaByUrlKey = new Map();
        for (const e of entries) {
            const urlKey = e.schema.urlKey ?? e.schema.key;
            schemaByUrlKey.set(urlKey, e.schema);
        }
        // default = push (если schema не найдена/не задано)
        let hasPush = false;
        let hasReplace = false;
        let hasRedirect = false;
        for (const k of touched) {
            const s = schemaByUrlKey.get(k);
            const m = s?.urlCommitMode ?? "push";
            if (m === "redirect")
                hasRedirect = true;
            else if (m === "push")
                hasPush = true;
            else
                hasReplace = true;
        }
        if (hasRedirect)
            return "redirect";
        if (hasPush)
            return "push";
        if (hasReplace)
            return "replace";
        return "push";
    }
    /**
     * Клонировать query string в `URLSearchParams`.
     * @param search Строка вида `?a=1&b=2`
     */
    _cloneSearch(search) {
        return new URLSearchParams(search?.startsWith("?") ? search.slice(1) : (search || ""));
    }
    /**
      * Применить параметры к `URLSearchParams` и `pathname`.
      * @param sp Search params
      * @param pathname Текущий pathname
      * @param params Новые параметры (urlKey -> rawValue)
      * @param removedKeys Ключи, которые нужно удалить
      * @param schemaByUrlKey Карта схем по urlKey
      */
    _apply(sp, pathname, params, removedKeys, schemaByUrlKey, mode) {
        let resultMode = mode;
        // сначала удаляем
        for (const urlKey of removedKeys) {
            const schema = schemaByUrlKey.get(urlKey);
            const loc = this._getUrlLocation(schema);
            if (loc === "path")
                pathname = this._writePathValue(pathname, schema, null);
            else
                sp.delete(urlKey);
        }
        // потом применяем новые значения
        for (const urlKey in params) {
            const schema = schemaByUrlKey.get(urlKey);
            const val = params[urlKey];
            if (schema) {
                const custom = PresetRegistry.default.onFilterAdded(schema, val, params);
                if (custom?.execRedirect && custom.targetPathname) {
                    resultMode = 'redirect';
                    pathname = custom.targetPathname;
                }
            }
            const loc = this._getUrlLocation(schema);
            if (loc === "path")
                pathname = this._writePathValue(pathname, schema, val);
            else
                sp.set(urlKey, val);
        }
        return { spNext: sp, pathnameNext: pathname, nextMode: resultMode };
    }
    /** Собрать URL (pathname + ?query + #hash). */
    _buildUrl(pathname, sp) {
        const qs = sp.toString();
        return `${pathname}${qs ? `?${qs}` : ""}${location.hash}`;
    }
    /** Текущий URL (pathname + search + hash). */
    _currentUrl() {
        return `${location.pathname}${location.search}${location.hash}`;
    }
    /** Записать URL в history или выполнить навигацию. */
    _commit(url, mode) {
        if (mode === "redirect") {
            window.location.assign(url);
            return;
        }
        if (mode === "replace")
            history.replaceState(null, "", url);
        else
            history.pushState(null, "", url);
    }
    /**
     * Закодировать значение в строку URL на основании схемы поля.
     * @param value Сырое значение
     * @param schema Схема поля
     */
    _encodeValue(value, schema) {
        // массив: кодируем КАЖДЫЙ элемент
        if (schema.isArray) {
            const items = parseBraceOrCsv(value);
            if (!items.length)
                return null;
            const mapped = schema.encMethod
                ? items.map(x => _enc(x, schema.encMethod))
                : items;
            return encodeArrayField(mapped);
        }
        switch (schema.type) {
            case FilterFieldTypes.boolean: {
                // принимаем "true"/"false"/"1"/"0"
                const v = value === "1" || value === "true";
                return v ? "1" : null; // false → не пишем в URL
            }
            case FilterFieldTypes.dateRange:
            case FilterFieldTypes.numberRange:
                return value;
            default:
                return _enc(value);
        }
    }
    /** Декодирование URL → состояние  */
    decode(search, entries) {
        const sp = this._cloneSearch(search);
        const out = {};
        for (const e of entries) {
            const schema = e.schema;
            const urlKey = schema.urlKey ?? schema.key;
            const loc = this._getUrlLocation(schema);
            const raw = loc === "path"
                ? this._readPathValue(location.pathname, schema)
                : sp.get(urlKey);
            if (raw === null) {
                // Нет параметра в URL → применяем default (если задан), иначе "сбросить"
                const def = schema.default;
                out[schema.key] = def == null ? null : String(def);
                continue;
            }
            // Параметр в URL есть (в т.ч. может быть пустой строкой)
            if (schema.isArray) {
                const allRaw = loc === "path" ? [raw] : sp.getAll(urlKey);
                const items = [];
                for (const part of allRaw) {
                    const parsed = parseBraceOrCsv(part);
                    if (schema.encMethod) {
                        for (const x of parsed)
                            items.push(_dec(x, schema.encMethod));
                    }
                    else {
                        items.push(...parsed);
                    }
                }
                const safe = [];
                for (const v of items) {
                    const ok = this._sanitizeDecoded(v, schema);
                    if (ok != null)
                        safe.push(ok);
                }
                out[schema.key] = safe.length ? safe : null;
            }
            else {
                const decoded = schema.encMethod ? _dec(raw, schema.encMethod) : raw;
                out[schema.key] = this._sanitizeDecoded(decoded, schema);
            }
        }
        return out;
    }
    /** Примировать lastParams из текущего URL (scope = entries) */
    primeFromSearch(search, entries) {
        this._lastParams = this._readParamsFromUrl(search, location.pathname, entries);
    }
    /**
     * Нормализовать значение, восстановленное из URL.
     * Возвращает null, если значение недопустимо и не должно попадать в UI.
     */
    _sanitizeDecoded(raw, schema) {
        const s = String(raw ?? "").trim();
        if (s === "")
            return null;
        // number/int: валидируем числом + диапазон (если задан)
        if (schema.type === FilterFieldTypes.int || schema.type === FilterFieldTypes.number) {
            const n = Number(s);
            if (!isFinite(n))
                return null;
            // int: строго целое
            if (schema.type === FilterFieldTypes.int && !Number.isInteger(n))
                return null;
            if (schema.min != null && n < schema.min)
                return null;
            if (schema.max != null && n > schema.max)
                return null;
            return String(n);
        }
        return s;
    }
    /** Прочитать текущие значения scope-ключей из URL: urlKey -> rawValue */
    _readParamsFromUrl(search, pathname, entries) {
        const sp = this._cloneSearch(search);
        const out = {};
        for (const e of entries) {
            const schema = e.schema;
            const urlKey = schema.urlKey ?? schema.key;
            const loc = this._getUrlLocation(schema);
            const v = loc === "path"
                ? this._readPathValue(pathname, schema)
                : sp.get(urlKey);
            if (v !== null)
                out[urlKey] = v;
        }
        return out;
    }
    /** Прочитать значение из pathname через schema.urlPathRegex (group 1 = value). */
    _readPathValue(pathname, schema) {
        if (!schema.urlPathRegex)
            return null;
        const re = new RegExp(schema.urlPathRegex);
        // ⚠️ В проекте первый сегмент URL может быть языком (например: /ru/...)
        // Чтобы не "съедать" его в group(1) (когда regex слишком общий, типа ^/(.+)-page-(\d+)),
        // сначала пытаемся матчить pathname БЕЗ языкового префикса.
        // Если regex сам включает язык — матч без префикса не сработает, и мы упадём в fallback.
        const { prefix, rest } = this._splitLocalePrefix(pathname);
        const m = (prefix ? re.exec(rest) : null) ?? re.exec(pathname);
        if (!m)
            return null;
        // group 1 = value
        return m[1] ?? null;
    }
    /** Получить расположение значения в URL. */
    _getUrlLocation(schema) {
        return (schema?.urlLocation ?? "query");
    }
    /**
 * Записать значение в pathname.
 * Ожидание: schema.urlPathRegex матчится на pathname, group 1 = value.
 */
    _writePathValue(pathname, schema, value) {
        if (!schema?.urlPathRegex)
            return pathname;
        const re = new RegExp(schema.urlPathRegex);
        const { prefix, rest } = this._splitLocalePrefix(pathname);
        // см. комментарий в _readPathValue: сначала пытаемся работать с rest (без /{lang})
        // чтобы гарантированно не потерять язык.
        const target = prefix ? rest : pathname;
        if (!re.test(target) && !re.test(pathname))
            return pathname;
        const nextValue = value ?? (schema.default == null ? null : String(schema.default));
        if (nextValue == null)
            return pathname;
        const replaceIn = (src) => src.replace(re, (full, g1) => {
            // заменяем только value-part (group 1), остальную структуру сегмента сохраняем
            return full.replace(g1, nextValue);
        });
        // 1) если есть язык — пробуем заменить в rest
        if (prefix && re.test(rest)) {
            return prefix + replaceIn(rest);
        }
        // 2) fallback: работаем с pathname целиком (на случай regex, который учитывает язык)
        return replaceIn(pathname);
    }
    /**
     * Выделить языковой префикс первого сегмента URL.
     * Пример: "/ru/photos-page-1/" → { prefix: "/ru", rest: "/photos-page-1/" }.
     *
     * Правило строгое: язык = ровно 2 латинские буквы в нижнем регистре.
     * Это снижает риск ошибочно считать языком обычный маршрут типа "/vs/...".
     */
    _splitLocalePrefix(pathname) {
        const m = /^\/(\p{Ll}{2})(?=\/)/u.exec(pathname);
        if (!m)
            return { prefix: "", rest: pathname };
        const prefix = `/${m[1]}`;
        const rest = pathname.slice(prefix.length);
        return { prefix, rest: rest.startsWith("/") ? rest : "/" + rest };
    }
}
/** Кодирование значения для URL по схеме поля. */
const _enc = (v, m) => m === "guid64" ? (compressGuidToBase64Url(v) ?? v) : v;
/** Декодирование значения из URL по схеме поля. */
const _dec = (v, m) => m === "guid64" ? (decompressGuidFromBase64Url(v) ?? v) : v;

export { SearchUrlCodec };
